// Dependencies
import { type UseQueryResult, useQuery } from "@tanstack/react-query";

import type { IFeatureFlag } from "@performyard/models";

// Project Dependencies
import request from "../../api/services/base-request";

type FlagSlug = IFeatureFlag["_id"];

type FeatureFlagsResponse = {
	feature_flags: {
		_id: FlagSlug;
		name: string;
		type: "boolean" | "number" | "string";
		value: boolean | number | string;
		initial_value: boolean | number | string;
	}[];
	errors: string[];
	warnings: string[];
};

export const getCompanyFeatureFlags = async () => {
	const [data] = await request("/api/feature-flags");
	return data as FeatureFlagsResponse;
};

export const getFeatureFlagQuery = () => ({
	queryKey: ["feature-flags"],
	queryFn: () => getCompanyFeatureFlags(),
});

/**
 * Returns the value of a feature flag for the current company.
 *
 * example:
 *  const isReportingVisualsEnabled = useFeatureFlag("reporting-visuals", false);
 *
 * @deprecated use useFeatureFlagQuery instead
 */
export const useFeatureFlag = <T>(flagSlug: FlagSlug, notSetValue: T): T => {
	const { data } = useQuery(getFeatureFlagQuery());

	if (!data) return notSetValue;

	const flag = data.feature_flags?.find((flag) => flag._id === flagSlug);

	if (!flag) return notSetValue;

	return flag.value as T;
};

/**
 * Similar to the useFeatureFlag hook, but returns a query result. This is useful
 * if you need to conditionally render something based on the loading state of
 * the feature flag.
 *
 * Moving forward, we should use this in place of useFeatureFlag.
 */
export const useFeatureFlagQuery = <T>(flagSlug: FlagSlug, notSetValue: T): { value: T; query: UseQueryResult } => {
	const result = useQuery(getFeatureFlagQuery());

	if (!result.data) return { value: notSetValue, query: result };

	const flag = result.data.feature_flags?.find((flag) => flag._id === flagSlug);

	if (!flag) return { value: notSetValue, query: result };

	return { value: flag.value as T, query: result };
};

/**
 * Hook that checks if the company has private goal alignment enabled.
 *
 * @returns Object with `value` (boolean) and `query` (UseQueryResult)
 */
export const useHasPrivateGoalAlignment = (): { value: boolean; query: UseQueryResult } => {
	const result = useQuery(getFeatureFlagQuery());

	if (!result.data) return { value: false, query: result };

	const featureFlag = result.data.feature_flags?.find((flag) => flag._id === "ff-has-private-goal-alignment");

	return { value: featureFlag?.value === true, query: result };
};
